import { useState, useCallback, useEffect, useRef } from 'react';
import { INITIAL_FORM_DATA, initialNFAIProductivityStatusCreate, NewTimeSheetEntry, NFAIProductivityStatusCreate } from '../types';

export const useFormData = () => {
  const [formData, setFormData] = useState<NewTimeSheetEntry>(INITIAL_FORM_DATA);
  const [actionHistory, setActionHistory] = useState<NFAIProductivityStatusCreate>(initialNFAIProductivityStatusCreate);

  const updateFormField = useCallback(
    <K extends keyof NewTimeSheetEntry>(
      field: K,
      value: NewTimeSheetEntry[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setActionHistory(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const populateFormFromEntry = useCallback((entry: NewTimeSheetEntry) => {
    const params: NewTimeSheetEntry = {
      ...INITIAL_FORM_DATA,
      id: entry.id,
      projectID: entry.projectID,
      project_Name: entry.project_Name,
      department: entry.department,
      emp_id: entry.emp_id,
      start_Reel: entry.start_Reel || 0,
      jumbo_Reel: entry.jumbo_Reel || "",
      end_Reel: entry.end_Reel || 0,
      comp_Reel: entry.comp_Reel || 0,
      start_Time: entry.start_Time,
      end_Time: entry.end_Time,
      duration: entry.duration,
      status: entry.status,
      task: entry.task,
      scope_of_Work: entry.scope_of_Work,
      dpX_Source_In: entry.dpX_Source_In || '',
      dpX_Source_Out: entry.dpX_Source_Out || '',
      error_Duration: entry.error_Duration || '',
      moV_Record_In: entry.moV_Record_In || '',
      moV_Record_Out: entry.moV_Record_Out || '',
      no_of_Corrections: entry.no_of_Corrections || 0,
      types_of_Correction: entry.types_of_Correction || '',
      error_Code: entry.error_Code || '',
      error_Remarks: entry.error_Remarks || '',
      remarks: entry.remarks || '',
      actions: entry.actions || '',
      action_Name: entry.action_Name || '',
    };
    setFormData(params);
  }, []);

  return { formData, actionHistory, updateFormField, resetForm, populateFormFromEntry };
};

export const createHandlers = (updateFormField: Function) => ({
  handleNumberInput: (field: string, value: string) => {
    const parsedValue = value === '' ? 0 : parseInt(value, 10);
    updateFormField(field, isNaN(parsedValue) ? 0 : parsedValue);
  },
});

// OPTIMIZED: Only update when needed for time-dependent actions
export const useCurrentDateTime = () => {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Only update every 30 seconds instead of every second
    // Or better yet, only get current time when actually needed
    intervalRef.current = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 30000); // 30 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Expose method to get fresh datetime on-demand
  const getCurrentDateTime = useCallback(() => new Date(), []);

  return { currentDateTime, getCurrentDateTime };
};